import { useState, useEffect, useRef } from "react";
import {
  FileText,
  Plus,
  Pencil,
  Trash2,
  Upload,
  X,
  Calendar,
  ExternalLink,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { getBlogPosts, createBlogPost, updateBlogPost, deleteBlogPost } from "../lib/api";
import { toast } from "sonner";

export default function BlogManage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    image: "",
    author: "Tournament Admin",
  });
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await getBlogPosts();
      setPosts(response.data);
    } catch (error) {
      toast.error("Failed to fetch blog posts");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleOpenDialog = (post = null) => {
    if (post) {
      setSelectedPost(post);
      setFormData({
        title: post.title,
        content: post.content,
        image: post.image || "",
        author: post.author || "Tournament Admin",
      });
    } else {
      setSelectedPost(null);
      setFormData({
        title: "",
        content: "",
        image: "",
        author: "Tournament Admin",
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedPost(null);
    setFormData({ title: "", content: "", image: "", author: "Tournament Admin" });
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be less than 5MB");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({ ...prev, image: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error("Title and content are required");
      return;
    }

    setSaving(true);
    try {
      if (selectedPost) {
        await updateBlogPost(selectedPost.id, formData);
        toast.success("Article updated successfully");
      } else {
        await createBlogPost(formData);
        toast.success("Article published successfully");
      }
      handleCloseDialog();
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save article");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPost) return;

    try {
      await deleteBlogPost(selectedPost.id);
      toast.success("Article deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedPost(null);
      fetchPosts();
    } catch (error) {
      toast.error("Failed to delete article");
    }
  };

  const openDeleteDialog = (post) => {
    setSelectedPost(post);
    setDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="blog-manage-page" className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold uppercase tracking-tight">
            Blog
          </h1>
          <p className="text-muted-foreground mt-2">
            Write weekly articles about the tournament
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="secondary"
            onClick={() => window.open("/blog", "_blank")}
            data-testid="view-blog-btn"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            View Public Blog
          </Button>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider btn-glow"
            data-testid="new-article-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Article
          </Button>
        </div>
      </div>

      {/* Posts List */}
      {posts.length > 0 ? (
        <div className="space-y-4">
          {posts.map((post, index) => (
            <div
              key={post.id}
              className="bg-card border border-border rounded-xl overflow-hidden card-hover animate-fade-in opacity-0"
              style={{ animationDelay: `${index * 30}ms` }}
              data-testid={`blog-manage-post-${post.id}`}
            >
              <div className="flex flex-col md:flex-row">
                {/* Image */}
                <div className="md:w-48 md:h-32 flex-shrink-0">
                  {post.image ? (
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-32 md:h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-32 md:h-full bg-secondary flex items-center justify-center">
                      <FileText className="w-10 h-10 text-muted-foreground/30" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 p-4 md:p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading text-lg font-bold uppercase tracking-wider mb-1 truncate">
                        {post.title}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                        {post.content}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(post.created_at)}
                        </span>
                        <span>By {post.author}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDialog(post)}
                        data-testid={`edit-post-${post.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDeleteDialog(post)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        data-testid={`delete-post-${post.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <FileText className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-bold uppercase mb-2">
            No Articles Yet
          </h3>
          <p className="text-muted-foreground mb-6">
            Write your first article to share tournament news and updates
          </p>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider btn-glow"
            data-testid="empty-new-article-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Write First Article
          </Button>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="bg-card border-border sm:max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="blog-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold uppercase tracking-wider">
              {selectedPost ? "Edit Article" : "New Article"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Title *
              </Label>
              <Input
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                placeholder="Article title"
                className="bg-background border-border"
                data-testid="article-title-input"
              />
            </div>

            {/* Author */}
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Author
              </Label>
              <Input
                value={formData.author}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, author: e.target.value }))
                }
                placeholder="Author name"
                className="bg-background border-border"
                data-testid="article-author-input"
              />
            </div>

            {/* Image */}
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Cover Image
              </Label>
              <div className="space-y-4">
                {formData.image && (
                  <div className="relative">
                    <img
                      src={formData.image}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-xl border border-border"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, image: "" }))
                      }
                      className="absolute top-2 right-2 w-8 h-8 bg-destructive text-white rounded-full flex items-center justify-center"
                      data-testid="remove-image-btn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full"
                  data-testid="upload-image-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {formData.image ? "Change Image" : "Upload Image"}
                </Button>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Content *
              </Label>
              <Textarea
                value={formData.content}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, content: e.target.value }))
                }
                placeholder="Write your article content here..."
                className="bg-background border-border min-h-[200px]"
                data-testid="article-content-input"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={handleCloseDialog}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider"
                data-testid="save-article-btn"
              >
                {saving ? "Saving..." : selectedPost ? "Update" : "Publish"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl font-bold uppercase">
              Delete Article
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedPost?.title}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-article-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
